import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener,
  Input, OnChanges, Output, SimpleChanges, ViewChild
} from '@angular/core';

export interface FilterValueOption {
  value: string;
  label: string;
}

/** Past this many options the list gets a search box. */
const SEARCH_THRESHOLD = 7;
/** Rows drawn at once. A supplier list runs to thousands; nobody scrolls that, they type. */
const MAX_ROWS = 100;

/**
 * The picker the filter panels (Inbox, Bills to Pay, Transactions) use for a condition's
 * field, comparison and values. It replaces the native <select>, whose popup is the
 * browser's own: it opened as an empty grey box until the page behind it had finished
 * working, could not be searched, and held one value.
 *
 * The list opens in the flow of the panel rather than floating over it, so it is never
 * clipped by the drawer's scroll and needs no positioning.
 *
 *   single  <app-filter-value-select [options]="ops" [value]="c.op" (valueChange)="...">
 *   multi   <app-filter-value-select [multiple]="true" [options]="tags" [values]="c.values"
 *                                    (valuesChange)="...">
 */
@Component({
  selector: 'app-filter-value-select',
  templateUrl: './filter-value-select.component.html',
  styleUrls: ['./filter-value-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterValueSelectComponent implements OnChanges {
  @Input() options: FilterValueOption[] = [];
  @Input() multiple = false;
  /** Single mode. */
  @Input() value = '';
  /** Multiple mode. */
  @Input() values: string[] = [];
  @Input() placeholder = 'Select a value';
  /** What the control is, for assistive tech and the tooltip. */
  @Input() label = 'Value';

  @Output() valueChange = new EventEmitter<string>();
  @Output() valuesChange = new EventEmitter<string[]>();

  @ViewChild('search') searchInput: ElementRef<HTMLInputElement>;

  isOpen = false;
  query = '';
  /** The rows on screen: the options that match the search, capped at MAX_ROWS. */
  rows: FilterValueOption[] = [];
  /** How many matched, which can be more than are drawn. */
  matchCount = 0;
  summary = '';
  hasSelection = false;

  private selected = new Set<string>();

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['values'] || changes['multiple']) {
      this.selected = new Set(this.multiple ? (this.values || []) : (this.value ? [this.value] : []));
    }
    if (changes['options']) {
      this.rebuildRows();
    }
    this.rebuildSummary();
  }

  get searchable(): boolean {
    return (this.options || []).length > SEARCH_THRESHOLD;
  }

  get hiddenCount(): number {
    return this.matchCount - this.rows.length;
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  isSelected(option: FilterValueOption): boolean {
    return this.selected.has(option.value);
  }

  trackByValue(_index: number, option: FilterValueOption): string {
    return option.value;
  }

  toggleOpen(): void {
    this.isOpen ? this.close() : this.open();
  }

  private open(): void {
    this.isOpen = true;
    this.query = '';
    this.rebuildRows();
    if (this.searchable) {
      setTimeout(() => this.searchInput?.nativeElement.focus());
    }
  }

  close(): void {
    if (!this.isOpen) { return; }
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  onQuery(query: string): void {
    this.query = query;
    this.rebuildRows();
  }

  pick(option: FilterValueOption): void {
    if (!this.multiple) {
      this.close();
      if (option.value !== this.value) {
        this.valueChange.emit(option.value);
      }
      return;
    }

    const next = new Set(this.selected);
    next.has(option.value) ? next.delete(option.value) : next.add(option.value);
    // In the options' own order, so the summary does not depend on click order.
    this.valuesChange.emit((this.options || []).filter(o => next.has(o.value)).map(o => o.value)
      .concat(Array.from(next).filter(v => !(this.options || []).some(o => o.value === v))));
  }

  clear(): void {
    this.valuesChange.emit([]);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.isOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (this.isOpen) {
      event.stopPropagation();
      this.close();
    }
  }

  private rebuildRows(): void {
    const all = this.options || [];
    const needle = this.query.trim().toLowerCase();
    const matches = needle
      ? all.filter(o => String(o.label ?? '').toLowerCase().includes(needle))
      : all;

    this.matchCount = matches.length;
    this.rows = matches.length > MAX_ROWS ? matches.slice(0, MAX_ROWS) : matches;
  }

  private rebuildSummary(): void {
    const labelOf = (value: string) =>
      (this.options || []).find(o => o.value === value)?.label ?? value;

    const chosen = Array.from(this.selected);
    this.hasSelection = chosen.length > 0;

    if (!chosen.length) {
      this.summary = this.placeholder;
    } else if (chosen.length <= 2) {
      this.summary = chosen.map(labelOf).join(', ');
    } else {
      this.summary = `${labelOf(chosen[0])}, ${labelOf(chosen[1])} +${chosen.length - 2} more`;
    }
  }
}
